'use client'

// The allotment by the meadow: two greenhouses, three raised beds, and
// whatever is growing in them right now.
//
// Sam's model, laid out in Blender AT WORLD COORDINATES like the junk
// heap and the ruins, so this renders at IDENTITY. Wrapping it in a group
// at the garden POI would add the two together and put the greenhouses
// twenty metres into the meadow. Moving the allotment means moving it in
// Blender; moving where he STANDS to tend is the POI in sceneConfig.
//
// THE PLANTS ARE THE POINT. The sim has owned three beds and their clocks
// since the greenhouse was built (world/Garden.js: seed, sprout, growing,
// ripe, on real-hour clocks, lost if he does not come), and none of it
// has ever been visible. The entity has carried an empty `beds` array and
// a comment calling it "a mirror the viewer can draw from" the whole
// time. This is the component that draws from it.
//
// How the model's parts are matched to the sim's beds:
//
//   • Each plant is assigned to the bed whose footprint it stands in,
//     worked out from the geometry at load. Nothing is hardcoded, so a
//     re-export with more plants in more beds needs no code change here.
//   • A plant whose material says `_w_Fruit` only appears when the bed is
//     RIPE. The others carry the growing stages.
//   • Every bed carries every crop the sim can sow (5 Oct: strawberries
//     and lettuces made, Sam's tomatoes, peas and beans copied into the
//     beds that lacked them), and fruit_<crop>_<n> says which; only the
//     bed's crop grows. A strawberry bed used to grow tomatoes.
//   • sprout_<n> is a seedling, shown at its own size while the bed is
//     sprouting, in place of the grown plant scaled to a third.
//   • Growth scales the plant ABOUT ITS BASE, not its origin. These
//     origins sit mid-plant, so a plain scale would shrink a seedling up
//     into the air and leave its roots hanging; the node is lifted back
//     down by the same amount the base rose.
//
// What is deliberately not drawn yet: a wilting bed looks like a healthy
// one, because the plants of different beds share one material and
// tinting a dry bed would tint them all. The diary carries it until the
// beds get their own material clones.

import { useEffect, useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import useGameStore from '../../../stores/gameStore'
import { useSceneNodeMaterials } from '../hooks/useSceneNodeMaterials'
import { useModel } from '../hooks/useModel'
import { plantLook, cropOf, AUTHORED_CROPS } from '../../../lib/world/garden'

const MODEL = '/models/gardening.glb'

/**
 * Which bed each plant stands in, and where its base is.
 *
 * Done once per model load from the geometry itself: the beds' footprints
 * are their bounding boxes in XZ, and a plant belongs to the first bed
 * that contains its centre. The alternative was a table of node names in
 * this file, which would be wrong the first time Sam moved a plant.
 */
function survey(scene) {
  const beds = []
  const plants = []
  const box = new THREE.Box3()
  // setFromObject reads world matrices, and nothing has rendered yet the
  // first time this runs. Without this every box comes back as the
  // untransformed geometry and every plant lands in the wrong bed.
  scene.updateMatrixWorld(true)

  scene.traverse((o) => {
    if (!o.isMesh && !o.isGroup) return
    const name = o.name || ''
    if (/^bed_\d+/i.test(name)) {
      box.setFromObject(o)
      beds.push({ node: o, index: Number(name.match(/\d+/)?.[0] ?? 0) - 1, min: box.min.clone(), max: box.max.clone() })
    }
  })

  scene.traverse((o) => {
    if (!o.isMesh) return
    const name = o.name || ''
    // Anything that is not a bed, a building or the dressing. The export
    // names the plants fruit_<crop>_<n>, and the seedlings sprout_<n>.
    const sprout = /^sprout_/i.test(name)
    if (!sprout && !/^fruit_/i.test(name)) return
    box.setFromObject(o)
    const cx = (box.min.x + box.max.x) / 2
    const cz = (box.min.z + box.max.z) / 2
    const bed = beds.find((b) => cx >= b.min.x && cx <= b.max.x && cz >= b.min.z && cz <= b.max.z)
    if (!bed) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    plants.push({
      node: o,
      bed: bed.index,
      // The fruited twin of the same plant. The export distinguishes them
      // by material, which survives a rename of the node.
      fruited: mats.some((m) => /_w_Fruit/i.test(m?.name || '')),
      // Every bed carries every crop since 5 Oct; the name says which
      crop: cropOf(name),
      sprout,
      // Where the plant meets the soil, and where its origin sits, both
      // in the parent's space. Growth is scaled about the first.
      baseOffset: box.min.y - o.position.y,
      originY: o.position.y,
    })
  })

  // Which beds have seedlings of their own, so a sprouting bed shows them
  // rather than the grown plant shrunk to a third
  const sprouting = new Set(plants.filter((p) => p.sprout).map((p) => p.bed))
  return { beds, plants, sprouting }
}

export function Greenhouse() {
  const { scene } = useModel(MODEL)
  useSceneNodeMaterials(scene, { roughness: 0.95 })

  const { plants, sprouting } = useMemo(() => (scene ? survey(scene) : { plants: [], sprouting: new Set() }), [scene])

  // The sim's beds, off the garden entity. Compared by what changes a
  // picture: a bed's crop and stage. Water and weeds move every tick and
  // redrawing for them would be a re-render a second for nothing.
  const bedStates = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates,
    (prev, next) => {
      const a = prev.find((e) => e.id === 'activity_garden')?.state?.beds || []
      const b = next.find((e) => e.id === 'activity_garden')?.state?.beds || []
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i++) {
        if (a[i]?.crop !== b[i]?.crop || a[i]?.stage !== b[i]?.stage) return false
      }
      return true
    },
  )

  // `known` is whether the sim has told us anything about this garden at
  // all, which is a different question from whether the beds are empty.
  const { growth, known } = useMemo(() => {
    const entity = bedStates.find((e) => e.id === 'activity_garden')
    const row = entity?.state?.beds
    return { growth: Array.isArray(row) ? row : [], known: !!entity }
  }, [bedStates])

  useEffect(() => {
    if (!plants.length) return
    for (const p of plants) {
      const look = plantLook(p, growth.find((b) => b.i === p.bed), { known, sprouts: sprouting.has(p.bed), authored: AUTHORED_CROPS[p.bed] || null })
      p.node.visible = look.show
      // Scale 0 would be a degenerate matrix, so a plant that is not
      // showing leaves the scene rather than shrinking to a point.
      if (!look.show) continue
      p.node.scale.setScalar(look.scale)
      p.node.position.y = look.y
    }
  }, [plants, sprouting, growth, known])

  return <primitive object={scene} />
}

export default Greenhouse
